import React, {Children, ReactNode} from 'react'
import {entries, fromEntries, isArray, isString, partition} from 'remeda'
import {compactObj, prefixKeys} from 'shared/collection'
import {doTo, doTry} from 'shared/promise'
import {Uri, uri} from 'shared/uri'
import {MarkdownOptions, Mention, defaultMarkdownOptions} from 'shared/ui'
import {P, match} from 'ts-pattern'
import {MentionProfile, MentionQuestion} from './form/lexical/MentionNode'
import {z} from 'zod'
import {ImageNodeProps} from './form/lexical/image'

export namespace ImgURL {
  const zImgQuery = z.object({
    width: z
      .string()
      .optional()
      .transform(n => (n ? parseInt(n) : undefined)),
    align: z.enum(['left', 'center', 'right']).optional()
  })

  /**  returns the url sans swift prams, and parsed swift data */
  export const parse = (src: string) => {
    const [swift, other] = partition(entries(new Uri(src).parts.query ?? {}), ([k]) => k.startsWith('swift-'))
    const data = zImgQuery.parse(fromEntries(swift.map(([k, v]) => [k.substring(6), v])))
    return {data, src: uri(src).with({query: fromEntries(other)}).url}
  }

  export const merge = (url: string, {align, width}: Pick<ImageNodeProps, 'width' | 'align'>) =>
    uri(url).mergeQuery(compactObj(prefixKeys({align, width: width?.toString()}, 'swift-'))).url
}

type MarkdownLinkProps = {
  node?: {properties?: React.ComponentProps<'a'>}
  children: ReactNode
  href?: string
}

type MarkdownImageProps = {
  node?: {properties?: React.ComponentProps<'img'>}
  src?: string
  alt?: string
}

export const markdownProps = () =>
  ({
    ...defaultMarkdownOptions,
    components: {
      ...defaultMarkdownOptions.components,
      a: ({node, children, href: rawHref}: MarkdownLinkProps) => {
        const props = node?.properties
        const schema = props?.href ? doTry(() => new URL(props.href!).protocol) : undefined
        const standard = () => (
          <a {...props} target='_blank'>
            {children}
          </a>
        )
        if (schema != 'swift:') return standard()
        const m = props?.href ? Mention.fromUrl(props.href) : undefined
        return match(m)
          .with({type: 'question'}, m => <MentionQuestion path={m.path}>{children}</MentionQuestion>)
          .with({type: 'profile'}, m =>
            isArray(children) && isString(children[0]) ?
              <MentionProfile path={m.path} title={children[0]} />
            : <>{children}</>
          )
          .otherwise(() => standard())
      },
      img: ({node, src: srcRaw, alt}: MarkdownImageProps) => {
        const props = node?.properties
        const {src, data} = isString(srcRaw) ? ImgURL.parse(srcRaw) : {src: undefined, data: undefined}
        return (
          <div
            className='flex'
            style={{
              ...(data ?
                {
                  justifyContent: match(data.align)
                    .with('left', () => 'start')
                    .with('right', () => 'end')
                    .otherwise(() => 'center')
                }
              : {})
            }}>
            <img
              {...props}
              src={src}
              alt={alt}
              className='max-w-full h-auto'
              style={
                data ?
                  compactObj({
                    width: data.width ? `${data.width}px` : undefined
                  })
                : undefined
              }
            />
          </div>
        )
      }
    }
  }) as MarkdownOptions
